import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Newspaper, Calendar, ArrowRight, CheckCircle, TrendingUp } from 'lucide-react';

const News = () => {
  const newsItems = [
    {
      id: 1,
      title: 'New UK Gambling Regulations: What Players Need to Know',
      excerpt: 'The latest updates from the UK Gambling Commission bring enhanced player protections and new responsible gambling measures. Learn how these changes affect your gaming experience.',
      date: '28.10.2025',
      slug: 'new-uk-gambling-regulations-2025',
      category: 'Regulations'
    },
    {
      id: 2,
      title: 'Top 5 Slot Games Dominating UK Casinos in 2025',
      excerpt: 'Discover the most popular slot titles that have taken the UK online casino scene by storm this year, featuring cutting-edge graphics and innovative bonus features.',
      date: '28.10.2025',
      slug: 'top-5-slot-games-2025',
      category: 'Games'
    },
    {
      id: 3,
      title: 'Understanding Casino Bonus Wagering Requirements',
      excerpt: 'A comprehensive guide to bonus terms and conditions, helping you understand wagering requirements and how to maximize your bonus value effectively.',
      date: '28.10.2025',
      slug: 'casino-bonus-wagering-requirements-guide',
      category: 'Guides'
    },
    {
      id: 4,
      title: 'UK Online Casino Market Growth: Record Player Numbers in 2025',
      excerpt: 'Industry analysis reveals unprecedented growth in UK online casino participation, with record numbers of licensed players and increased average spending patterns.',
      date: '27.10.2025',
      slug: 'uk-online-casino-market-growth-2025',
      category: 'Industry'
    },
    {
      id: 5,
      title: 'Payment Innovation: New Cryptocurrency Options for UK Players',
      excerpt: 'Leading UK casinos announce expanded cryptocurrency payment options, offering faster withdrawals and enhanced privacy for players seeking alternative payment methods.',
      date: '26.10.2025',
      slug: 'payment-innovation-cryptocurrency-options-uk',
      category: 'Payments'
    },
    {
      id: 6,
      title: 'Live Dealer Technology: Revolutionary Changes Coming to UK Casinos',
      excerpt: 'Next-generation live dealer studios with enhanced streaming quality, new game variants, and immersive features are transforming the live casino experience for UK players.',
      date: '25.10.2025',
      slug: 'live-dealer-technology-revolutionary-changes',
      category: 'Technology'
    },
    {
      id: 7,
      title: 'Responsible Gambling Initiatives: New Tools and Support Programs',
      excerpt: 'UK Gambling Commission introduces enhanced responsible gambling tools, expanded support services, and new player protection measures across licensed operators.',
      date: '24.10.2025',
      slug: 'responsible-gambling-initiatives-new-tools',
      category: 'Responsible Gaming'
    },
    {
      id: 8,
      title: 'Mobile Gaming Dominance: Smartphone Casino Usage Reaches New Heights',
      excerpt: 'Statistics reveal that mobile devices now account for over 75% of UK online casino traffic, driving innovation in mobile-first design and app development.',
      date: '23.10.2025',
      slug: 'mobile-gaming-dominance-smartphone-casino-usage',
      category: 'Mobile'
    },
    {
      id: 9,
      title: 'Slot Game Providers: New Partnerships Shape UK Casino Landscape',
      excerpt: 'Major software providers announce exclusive partnerships with UK casinos, bringing innovative game mechanics, higher RTPs, and unique bonus features to British players.',
      date: '22.10.2025',
      slug: 'slot-game-providers-new-partnerships-uk',
      category: 'Games'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-pink-500 to-green-500 rounded-full mb-6">
              <Newspaper className="w-10 h-10 text-white" />
            </div>
            <h1 className="pb-2 text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
              UK Online Casino News & Industry Updates
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Stay informed with the latest developments, regulatory changes, industry trends, and expert insights shaping the UK online casino landscape.
            </p>
          </div>

          {/* News Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {newsItems.map((news) => (
              <article key={news.id} itemScope itemType="https://schema.org/NewsArticle" className="glass-strong rounded-3xl overflow-hidden group hover:transform hover:scale-105 transition-all duration-300 flex flex-col">
                <Link
                  to={`/news/${news.slug}`}
                  className="flex flex-col flex-grow"
                  data-discover="true"
                  itemProp="url"
                >
                  {/* News Image */}
                  <div className="relative h-48 bg-gradient-to-br from-pink-200 via-green-200 to-pink-300 overflow-hidden" style={{ backgroundImage: `url("/images/${news.id === 1 ? 'marin-tulard-9sbO_vzyG40-unsplash.jpg' : news.id === 2 ? 'markus-spiske-khurOlBGbQ8-unsplash.jpg' : news.id === 3 ? 'point-normal-44cKaUuRmBg-unsplash.jpg' : news.id === 4 ? 'stephan-coudassot-saitqe6-Oyo-unsplash.jpg' : news.id === 5 ? 'aldric-rivat-CltI5xgDAs0-unsplash.jpg' : news.id === 6 ? 'amol-tyagi-7dkLbNE6ycI-unsplash.jpg' : news.id === 7 ? 'derek-lynn-mD1V-eS1Wb4-unsplash.jpg' : news.id === 8 ? 'eyestetix-studio-UxqoF0LfHB8-unsplash.jpg' : 'aidan-howe-xrR-Kr2zppo-unsplash.jpg'}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="glass px-3 py-1 rounded-full text-white text-xs font-semibold" itemProp="articleSection">
                        {news.category}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 flex items-center space-x-2 text-white/90 text-sm">
                      <Calendar className="w-4 h-4" />
                      <time itemProp="datePublished" dateTime="2025-10-28">
                        <span>{news.date}</span>
                      </time>
                    </div>
                  </div>

                  <div className="p-6 flex-grow flex flex-col">
                    <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600 transition-colors line-clamp-2" itemProp="headline">
                      {news.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed flex-grow line-clamp-3" itemProp="description">
                      {news.excerpt}
                    </p>
                    <div className="flex items-center text-green-600 font-semibold group-hover:translate-x-2 transition-transform">
                      <span className="text-sm">Read More</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </Link>
                <div itemProp="publisher" itemScope itemType="https://schema.org/Organization" style={{ display: 'none' }}>
                  <span itemProp="name">Crown Jackpot</span>
                </div>
              </article>
            ))}
          </div>

          {/* How We Choose News Section */}
          <div className="glass-strong rounded-3xl p-8 md:p-12 mb-16">
            <div className="flex items-center space-x-3 mb-6">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How We Choose Our News</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p>
                At Crown Jackpot, our news selection process reflects our commitment to delivering accurate, relevant, and valuable information that genuinely serves UK online casino players. We don't simply aggregate headlines or republish press releases – we curate news through a rigorous editorial process that evaluates relevance, accuracy, impact, and value to our audience.
              </p>

              <p>
                Our editorial team monitors numerous sources including UK Gambling Commission announcements, regulatory consultations, industry publications, operator press releases, player forums, and independent research. However, monitoring is just the beginning. Every potential news story undergoes evaluation against our editorial criteria: direct relevance to UK players, potential impact on gaming experience, accuracy and verifiability, timeliness and current relevance, and actionable value for our audience.
              </p>

              <p>
                We prioritize news that directly affects player experiences: regulatory changes that alter how casinos operate, new responsible gambling tools that protect players, payment innovations that improve convenience, game releases that enhance entertainment options, industry developments that signal market trends, and player protection measures that increase safety. News that merely promotes specific operators or lacks clear player relevance doesn't meet our editorial standards.
              </p>

              <p>
                Accuracy verification represents a critical component of our selection process. Before publishing any news story, we verify facts through multiple independent sources, check regulatory documents directly, confirm information with primary sources when possible, and distinguish between confirmed developments and industry speculation. We clearly label opinion pieces, analysis, and speculation to ensure readers understand what's confirmed fact versus editorial interpretation.
              </p>

              <p>
                Our news coverage maintains complete editorial independence from commercial relationships. While we may feature news about casinos we review or recommend, this relationship never influences our news selection, coverage, or editorial tone. We cover both positive developments and concerning trends with equal objectivity, recognizing that players need balanced information to make informed decisions.
              </p>

              <p>
                We consider the practical implications of news for players. Regulatory changes might require understanding new procedures, industry trends could signal market shifts affecting player options, technology developments might enhance gaming experiences, and responsible gambling initiatives offer new protection tools. Our coverage explains not just what's happening, but how it affects players practically and what actions readers might consider.
              </p>

              <p>
                Timing matters significantly in news selection. We prioritize breaking developments that require immediate player awareness, such as regulatory enforcement actions, payment system changes, or security concerns. We also cover evolving stories that develop over time, providing updates as situations clarify. However, we avoid rushing to publish unverified information – accuracy always takes precedence over speed.
              </p>

              <p>
                Our news selection reflects understanding of the UK gambling industry's complexity. We recognize that regulatory, technological, and market developments interconnect, and we provide context that helps readers understand how different news items relate to broader industry trends. This contextual approach transforms isolated news items into comprehensive understanding of the UK online casino landscape.
              </p>

              <p>
                We maintain high standards for source credibility. Official regulatory announcements receive priority coverage, industry research from recognized organizations provides valuable insights, operator announcements undergo scrutiny for accuracy, and player experiences shared through reputable forums contribute to our understanding. We avoid sensationalism, unsubstantiated claims, and sources with clear commercial biases.
              </p>

              <div className="glass p-6 rounded-2xl my-8 border-l-4 border-green-500">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Editorial Standards</h3>
                    <p className="text-gray-700">
                      Every news story we publish must meet strict standards: verified accuracy through multiple sources, clear relevance to UK players, actionable value beyond mere information, balanced presentation of facts and context, and independence from commercial influence. These standards ensure our news coverage serves player interests above all else.
                    </p>
                  </div>
                </div>
              </div>

              <p>
                Our commitment to quality news extends beyond selection to presentation. We structure news stories to highlight key information quickly while providing depth for readers seeking comprehensive understanding. We explain industry terminology, provide background context, and connect news items to broader themes affecting UK online casino players. This approach makes complex industry developments accessible without oversimplification.
              </p>

              <p>
                Ultimately, our news selection process serves a single purpose: providing UK online casino players with information they need to make informed decisions, understand industry developments, and navigate the evolving landscape of online gambling. Every story we publish reflects this commitment to player value, editorial excellence, and independent journalism that prioritizes player interests above commercial considerations.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default News;
